import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

interface UrlsState {
  previousUrl: string | null;
  currentUrl: string | null;
  extra: null | (() => string);
}

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  private readonly router = inject(Router);

  private readonly urlsSubject = new BehaviorSubject<UrlsState>({
    previousUrl: null,
    currentUrl: null,
    extra: null,
  });
  public readonly urlsState$ = this.urlsSubject.asObservable();

  private readonly queryParamsByUrl = new Map<string, Params>();

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event) => this.storePreviousUrl(event as NavigationEnd)),
        tap(() => this.storeLastQueryParam()),
      )
      .subscribe();
  }

  public getUrlsState(): UrlsState {
    return this.urlsSubject.value;
  }

  public getQueryParamsHistory(): Map<string, Params> {
    return this.queryParamsByUrl;
  }

  public setExtraCallback(extra: (() => string) | null) {
    const previousUrl = this.urlsSubject.value.currentUrl;
    const currentUrl = this.urlsSubject.value.currentUrl;

    this.urlsSubject.next({ previousUrl, currentUrl, extra });
  }

  private storePreviousUrl(event: NavigationEnd): void {
    const previousUrl = this.urlsSubject.value.currentUrl;
    const extra = this.urlsSubject.value.extra;
    const currentUrl = (event as NavigationEnd).url;

    this.urlsSubject.next({ previousUrl, currentUrl, extra });
  }

  private storeLastQueryParam(): void {
    const { currentUrl } = this.getUrlsState();

    if (!currentUrl) {
      return;
    }

    const url = new URL(currentUrl, window.location.origin);

    const searchParamsObject: Record<string, string> = Object.fromEntries(
      Array.from(url.searchParams.entries()),
    ) as Record<string, string>;

    this.queryParamsByUrl.set(url.pathname, searchParamsObject);
  }
}
